import axios from 'axios';
import { API_BASE } from './apiConfig';

// Global Admin dashboard — user analytics, one function per tab, matching one endpoint each:
//   Overall    GET /api/admin/analytics/users/overview     [RequireAdminAccess(GlobalAdminOnly)]
//   CMS        GET /api/admin/analytics/users/cms          [RequireAdminAccess(GlobalAdminOnly)]
//   Immersive  GET /api/admin/analytics/users/immersive    [RequireAdminAccess(GlobalAdminOnly)]
//
// Array filters are serialised as repeated params (?entityIds=1&entityIds=2), matching the
// convention in paymentApi.js and binding to List<int> on the controller.
const repeatArrayParamsSerializer = (params) => {
  const s = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    Array.isArray(value) ? value.forEach((item) => s.append(key, item)) : s.append(key, value);
  });
  return s.toString();
};

/**
 * Overall tab — platform-wide counts by product. No entity/space axis.
 *
 * @param {object} filters
 * @param {string} [filters.from] 'YYYY-MM-DD' — send with `to` or omit both
 * @param {string} [filters.to]   'YYYY-MM-DD' inclusive
 * @param {string} [filters.emailDomain] substring match against email — guests have no
 *   email, so an active filter drives guestUsers to 0
 */
export const getOverviewAnalytics = ({ from, to, emailDomain } = {}) =>
  axios.get(`${API_BASE}/api/admin/analytics/users/overview`, {
    params: {
      ...(from && to ? { from, to } : {}),
      ...(emailDomain ? { emailDomain } : {}),
    },
    withCredentials: true,
  }).then((r) => r.data);

/**
 * CMS tab — cms_oid-based counts, attributed to Entity/Space via space_member.
 * EntityIds/SpaceIds narrow every field in the response, including the headline totals.
 *
 * @param {object}   filters
 * @param {number[]} [filters.entityIds] ignored when spaceIds is supplied
 * @param {number[]} [filters.spaceIds]
 * @param {string}   [filters.from]
 * @param {string}   [filters.to]
 * @param {string}   [filters.emailDomain] substring match against email — narrows the
 *   headline and both bars, same as entityIds/spaceIds
 */
export const getCmsAnalytics = ({ entityIds, spaceIds, from, to, emailDomain } = {}) =>
  axios.get(`${API_BASE}/api/admin/analytics/users/cms`, {
    params: {
      ...(spaceIds?.length  ? { spaceIds  } : {}),
      ...(entityIds?.length ? { entityIds } : {}),
      ...(from && to ? { from, to } : {}),
      ...(emailDomain ? { emailDomain } : {}),
    },
    paramsSerializer: repeatArrayParamsSerializer,
    withCredentials: true,
  }).then((r) => r.data);

/**
 * Immersive tab — oid-based (plus guest) counts, attributed to Entity/Space via
 * user_session_management (covers guests, unlike the CMS tab's space_member join). Also
 * returns dau/mau — activity as of now, independent of from/to, but still scoped by
 * entityIds/spaceIds.
 *
 * @param {object}   filters
 * @param {number[]} [filters.entityIds] ignored when spaceIds is supplied
 * @param {number[]} [filters.spaceIds]
 * @param {string}   [filters.from]
 * @param {string}   [filters.to]
 * @param {string}   [filters.emailDomain] substring match against email — narrows the
 *   headline, DAU/MAU, and both bars, same as entityIds/spaceIds
 */
export const getImmersiveAnalytics = ({ entityIds, spaceIds, from, to, emailDomain } = {}) =>
  axios.get(`${API_BASE}/api/admin/analytics/users/immersive`, {
    params: {
      ...(spaceIds?.length  ? { spaceIds  } : {}),
      ...(entityIds?.length ? { entityIds } : {}),
      ...(from && to ? { from, to } : {}),
      ...(emailDomain ? { emailDomain } : {}),
    },
    paramsSerializer: repeatArrayParamsSerializer,
    withCredentials: true,
  }).then((r) => r.data);

/**
 * KPI-card drill-down popup — one shared endpoint for all 21 buckets across the three tabs
 * (see src/constants/analyticsBuckets.js). entityIds/spaceIds are rejected by the backend on
 * `overview.*` buckets — callers must not pass them for those.
 *
 * @param {object}   filters
 * @param {string}   filters.bucket  one of DRILLDOWN_BUCKETS
 * @param {number[]} [filters.entityIds] cms and immersive buckets only, not overview
 * @param {number[]} [filters.spaceIds]
 * @param {string}   [filters.from]
 * @param {string}   [filters.to]
 * @param {string}   [filters.emailDomain] substring match against email — free text or a
 *   value picked from the response's `availableDomains`
 * @param {number}   [filters.page]     default 1
 * @param {number}   [filters.pageSize] default 20, max 100
 */
export const getUserDrilldown = ({ bucket, entityIds, spaceIds, from, to, emailDomain, page, pageSize } = {}) =>
  axios.get(`${API_BASE}/api/admin/analytics/users/drilldown`, {
    params: {
      bucket,
      ...(spaceIds?.length  ? { spaceIds  } : {}),
      ...(entityIds?.length ? { entityIds } : {}),
      ...(from && to ? { from, to } : {}),
      ...(emailDomain ? { emailDomain } : {}),
      ...(page     ? { page }     : {}),
      ...(pageSize ? { pageSize } : {}),
    },
    paramsSerializer: repeatArrayParamsSerializer,
    withCredentials: true,
  }).then((r) => r.data);

/**
 * Immersive-only D1/D7/D30 retention for the cohort of users whose first-ever session falls
 * in the given month. Its own endpoint, not a drilldown bucket — cohortMonth is an anchor
 * month, not a from/to window, unlike every other call in this file.
 *
 * @param {object}   filters
 * @param {string}   filters.cohortMonth 'YYYY-MM-DD' — any date within the target month,
 *   the backend normalizes to that month's 1st. Required.
 * @param {number[]} [filters.entityIds] ignored when spaceIds is supplied
 * @param {number[]} [filters.spaceIds]
 * @param {string}   [filters.emailDomain] substring match against email
 */
export const getRetentionAnalytics = ({ cohortMonth, entityIds, spaceIds, emailDomain } = {}) =>
  axios.get(`${API_BASE}/api/admin/analytics/users/retention`, {
    params: {
      cohortMonth,
      ...(spaceIds?.length  ? { spaceIds  } : {}),
      ...(entityIds?.length ? { entityIds } : {}),
      ...(emailDomain ? { emailDomain } : {}),
    },
    paramsSerializer: repeatArrayParamsSerializer,
    withCredentials: true,
  }).then((r) => r.data);

// ── Redesigned single-page analytics ─────────────────────────────────────────
// summary / list, one global filter set. Single ids, so no array serializer.
//
// `_silent: true` keeps these off the app-wide loading overlay: the page refreshes only its
// numbers, so a full-screen Loader on every filter change would defeat the point.
//
// The domain goes out as `emailDomain` — the API ignores unknown query names, so a misnamed
// param would make the filter silently do nothing. `period` is a token for presets (the server
// resolves the dates in ET); from/to are sent only for a custom range.

const clean = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));

const scopeParams = ({ entityId, spaceId, emailDomain, period, from, to } = {}) =>
  clean({ entityId, spaceId, emailDomain, period, ...(period === 'custom' ? { from, to } : {}) });

const getAnalytics = (path, params, signal) =>
  axios.get(`${API_BASE}/api/admin/analytics/users/${path}`, {
    params,
    withCredentials: true,
    signal,
    _silent: true,
  }).then((r) => r.data);

/** GET /summary — totals and DAU/WAU/MAU with deltas. The response also carries `monthlyGrowth`, which the CMS no longer shows. */
export const getUserAnalyticsSummary = (filters, { signal } = {}) =>
  getAnalytics('summary', scopeParams(filters), signal);

/**
 * GET /list — paged, searchable users behind one card.
 * activityFrom/activityTo change only the Total Sessions / Total Active Days columns.
 */
export const getUserAnalyticsList = ({ card, activityFrom, activityTo, page, pageSize, search, ...filters }, { signal } = {}) =>
  getAnalytics('list', clean({ card, ...scopeParams(filters), activityFrom, activityTo, page, pageSize, search }), signal);
